import { defineComponent, computed, onMounted, ref, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import {
  NavBar as VanNavBar,
  Icon as VanIcon,
  List as VanList,
  PullRefresh as VanPullRefresh,
  Row as VanRow,
  Col as VanCol,
  Swipe as VanSwipe,
  SwipeItem as VanSwipeItem,
  showToast,
} from 'vant'
import {
  useForumStore,
  type ForumBoard,
  type ForumLotteryFilter,
  type ForumPost,
  type ForumSort,
} from '@/stores/forum-store'
import { useAuthStore } from '@/stores/auth-store'
import { useFollowStore } from '@/stores/follow-store'
import { useSeo, useSiteBrand } from '@/composables/use-seo'
import { ExternalAnchor } from '@/components/external-anchor'
import {
  getApiV1CommunityForumPublishCheck,
  getApiV1ForumAnnouncements,
  getApiV1ForumAwardRankingYearPeriod,
  getApiV1ForumBanners,
  postApiV1UserFollow,
} from '@/api/sdk.gen'
import type { ForumAnnouncement, ForumBanner } from '@/api/types.gen'
import iconSearch from '@/assets/forum/search.png'
import iconPublish from '@/assets/forum/publish.png'
import iconTop from '@/assets/forum/top.png'
import iconEssence from '@/assets/forum/essence.png'
import {
  ForumUserImg,
  renderPostPictures,
  renderPostStats,
} from '@/components/forum-post-parts'

// ==================== 常量 ====================

const BOARDS: { type: ForumBoard; name: string }[] = [
  { type: 0, name: '全部主题' },
  { type: 1, name: '参赛贴' },
  { type: 2, name: '图解小组' },
]

const SORT_TABS: { type: ForumSort | 4; name: string }[] = [
  { type: 0, name: '综合' },
  { type: 1, name: '精华' },
  { type: 2, name: '最多赞' },
  { type: 3, name: '最新' },
  { type: 4, name: '规则' },
]

const LOTTERY_FILTER_TABS: { type: ForumLotteryFilter; label: string }[] = [
  { type: 'all', label: '全部' },
  { type: 'macau', label: '澳彩' },
  { type: 'hk', label: '港彩' },
]

// 首页跳转时传入 region=hk|aocai；论坛内部状态用 'hk'|'macau'|'all'。
function regionQueryToFilter(q: unknown): ForumLotteryFilter {
  if (q === 'hk') return 'hk'
  if (q === 'aocai') return 'macau'
  return 'all'
}

function filterToRegionQuery(f: ForumLotteryFilter): string | undefined {
  if (f === 'hk') return 'hk'
  if (f === 'macau') return 'aocai'
  return undefined
}

// ==================== 子组件 ====================

const PostCard = defineComponent({
  name: 'PostCard',
  props: {
    post: { type: Object as () => ForumPost, required: true },
    showContestBadge: { type: Boolean, default: false },
    concerned: { type: Boolean, default: false },
  },
  emits: ['go', 'toggle-concern'],
  setup(props, { emit }) {
    return () => {
      const { post, showContestBadge, concerned } = props
      return (
        <li>
          <div class="user-info-box">
            <ForumUserImg post={post} />
          </div>
          <div class="forumflow">
            <button type="button" class="btn-reset" onClick={() => emit('toggle-concern', post)}>
              {concerned ? '已关注' : '+ 关注'}
            </button>
          </div>

          <VanRow class="title" onClick={() => emit('go', post)}>
            <VanCol class="slabel">{post.periodStr}</VanCol>
            <VanCol span={21}>
              <div class="van19 van-ellipsis">
                {post.top === 1 && (
                  <span class="up">
                    <img src={iconTop} width="25" alt="置顶" />
                  </span>
                )}
                {showContestBadge && <span class="cans"> #参赛贴# </span>}
                <span class="van19">
                  {post.highQuality === 1 && <img class="icon" src={iconEssence} alt="精华" />}
                </span>
                <div class={['formtitle', post.read ? 'formtitle--read' : '']}>{post.title}</div>
                {post.read && <div class="reads">已读</div>}
              </div>
            </VanCol>
          </VanRow>

          {/* 摘要是 innerHTML，链接包在外面；点击仍走父组件的 go（先标已读再跳），href 给键盘和爬虫 */}
          <a
            class="btn-reset block"
            href={`/forum/detail/${post.postId}`}
            onClick={(e: MouseEvent) => {
              e.preventDefault()
              emit('go', post)
            }}
          >
            <div
              class={['text', 'van-multi-ellipsis--l3', post.read ? 'text--read' : '']}
              innerHTML={post.description}
            />
          </a>

          {post.pictureList && post.pictureList.length > 0 && (
            <div class="img-box">
              <div class="images">
                <VanRow class="img-box-3" gutter={10}>
                  {renderPostPictures(post.pictureList)}
                </VanRow>
              </div>
            </div>
          )}

          {renderPostStats(post)}
        </li>
      )
    }
  },
})

// ==================== 主页面 ====================

export default defineComponent({
  name: 'ForumPage',
  setup() {
    const router = useRouter()
    const route = useRoute()
    const store = useForumStore()
    const authStore = useAuthStore()
    const followStore = useFollowStore()

    // SEO：六合论坛首页（站点名取后台配置）
    const brand = useSiteBrand()
    useSeo({
      title: () => `六合论坛_高手心水交流资料分享 - ${brand.value}`,
      description: () =>
        `${brand.value}六合论坛，高手心水交流与资料分享社区，汇集各路高手的研究心得与资料。`,
      keywords: () => `六合论坛,高手心水,资料分享,交流社区,${brand.value}`,
    })

    // 初始：以 URL 中的 region 为准同步到 store（不触发 fetchList，随后 onMounted 统一拉一次）
    store.lotteryFilter = regionQueryToFilter(route.query.region)

    // ========== 顶部 info-box：广告 + 公告（两个 API 分别接入）==========
    // lotteryFilter='all' ⇒ 不传 lottery_type（仅返回全站）
    // 'hk' ⇒ 1（港彩，含全站）；'macau' ⇒ 2（澳彩，含全站）
    const infoBoxLotteryType = computed<0 | 1 | 2 | undefined>(() => {
      if (store.lotteryFilter === 'hk') return 1
      if (store.lotteryFilter === 'macau') return 2
      return undefined
    })

    // ----- 广告：/api/v1/forum/banners -----
    const bannerList = ref<ForumBanner[]>([])

    async function loadBanners() {
      const lottery_type = infoBoxLotteryType.value
      try {
        const res = await getApiV1ForumBanners({
          query: { page: 1, size: 50, ...(lottery_type === undefined ? {} : { lottery_type }) },
        })
        const list = res.data?.data?.list ?? []

        // 如果没有 https:// 的前缀，则补充上
        bannerList.value = list.map((b) => {
          if (b.url && !/^https?:\/\//.test(b.url)) {
            return { ...b, url: 'https://' + b.url }
          }
          return b
        })
      } catch {
        bannerList.value = []
      }
    }


    // ----- 公告：/api/v1/forum/announcements -----
    const announcementList = ref<ForumAnnouncement[]>([])

    async function loadAnnouncements() {
      const lottery_type = infoBoxLotteryType.value
      try {
        const res = await getApiV1ForumAnnouncements({
          query: { page: 1, size: 50, ...(lottery_type === undefined ? {} : { lottery_type }) },
        })
        announcementList.value = res.data?.data?.list ?? []
      } catch {
        announcementList.value = []
      }
    }

    // ========== 入口横幅（§2.3 参赛贴高手获奖榜单）==========
    // lotteryFilter='all' 时 fallback 到澳彩（与首页彩种切换默认对齐）
    const bannerLotteryType = computed<1 | 2>(() => (store.lotteryFilter === 'hk' ? 1 : 2))
    const bannerLotteryName = computed(() => (bannerLotteryType.value === 1 ? '香港' : '澳门'))
    const bannerYear = ref<number | null>(null)
    const bannerPeriod = ref<string | null>(null)

    async function loadBannerPeriod() {
      try {
        const res = await getApiV1ForumAwardRankingYearPeriod({
          query: { lottery_type: bannerLotteryType.value },
        })
        const list = res.data?.data?.list ?? []
        const first = list[0]
        if (first?.periods?.length) {
          bannerYear.value = first.year ?? null
          bannerPeriod.value = first.periods[0] ?? null
        } else {
          bannerYear.value = null
          bannerPeriod.value = null
        }
      } catch {
        bannerYear.value = null
        bannerPeriod.value = null
      }
    }

    const awardRankingTo = computed(() => ({
      path: '/forum/award-ranking',
      query: {
        lottery_type: String(bannerLotteryType.value),
        year: String(bannerYear.value),
        period: bannerPeriod.value,
      },
    }))

    onMounted(() => {
      store.fetchList(true)
      loadBannerPeriod()
      loadBanners()
      loadAnnouncements()
      if (authStore.isLoggedIn) followStore.fetchFollowedList()
    })

    // 登录态变化时同步关注列表（登入拉取，登出清空）
    watch(
      () => authStore.isLoggedIn,
      (logged) => {
        if (logged) followStore.fetchFollowedList(true)
        else followStore.reset()
      },
    )

    // 彩种切换时重新拉横幅期数
    watch(bannerLotteryType, () => loadBannerPeriod())

    // 彩种切换时分别刷新广告/公告
    watch(infoBoxLotteryType, () => {
      loadBanners()
      loadAnnouncements()
    })

    // URL 变化 → 同步到 store（浏览器前进/后退、外部跳转）
    watch(
      () => route.query.region,
      (r) => {
        const next = regionQueryToFilter(r)
        if (next !== store.lotteryFilter) store.setLotteryFilter(next)
      },
    )

    const onLotteryFilterClick = (type: ForumLotteryFilter) => {
      if (store.lotteryFilter === type) return
      store.setLotteryFilter(type)
      const region = filterToRegionQuery(type)
      const query = { ...route.query }
      if (region === undefined) delete query.region
      else query.region = region
      router.replace({ path: '/forum', query })
    }

    const onBoardChange = (b: ForumBoard) => store.setBoard(b)

    async function onToggleConcern(post: ForumPost) {
      if (!authStore.isLoggedIn) {
        router.push('/login')
        return
      }
      const uid = Number(post.userId)
      if (!uid) return
      const prev = followStore.isFollowing(uid)
      followStore.setFollowed(uid, !prev)
      try {
        await postApiV1UserFollow({ body: { follow_user_id: uid } })
      } catch {
        followStore.setFollowed(uid, prev)
        showToast('操作失败')
      }
    }

    const refreshing = ref(false)

    const onRefresh = async () => {
      await store.fetchList(true)
      refreshing.value = false
    }

    const onLoadMore = () => {
      store.fetchList(false)
    }

    const onGoDetail = (post: ForumPost) => {
      store.markRead(post.postId)
      router.push(`/forum/detail/${post.postId}`)
    }

    async function onGoPublish() {
      if (!authStore.isLoggedIn) {
        router.push('/login')
        return
      }
      try {
        const res = await getApiV1CommunityForumPublishCheck()
        const check = res.data?.data
        if (check?.allow === false) {
          showToast(check.msg || '注册满24小时后才能发帖')
          return
        }
      } catch {
        showToast('操作失败')
        return
      }
      router.push('/forum/publish')
    }

    return () => (
      <div class="forum-body">
        <VanNavBar
          fixed
          placeholder
          border={false}
          title="六合论坛"
          onClickLeft={() => router.push('/')}
        >
          {{
            left: () => <VanIcon name="arrow-left" color="#fff" size="16" />,
            right: () => (
              <div class="forum-nav-right">
                <RouterLink class="btn-reset flex" to="/forum/search">
                  <img class="icon-right" src={iconSearch} alt="搜索" />
                </RouterLink>
                <button type="button" class="btn-reset flex" onClick={onGoPublish}>
                  <img class="icon-right" src={iconPublish} alt="发帖" />
                </button>
              </div>
            ),
          }}
        </VanNavBar>

        {/* 参赛贴高手获奖榜单入口横幅（49208 chunk-forum 同款 cansai 结构 + 金色渐变） */}
        {(bannerPeriod.value ||
          bannerList.value.length > 0 ||
          announcementList.value.length > 0) && [
            <div class="forum-body-hd">
              {(bannerList.value.length > 0 || announcementList.value.length > 0) && (
                <div class="info-box">
                  {bannerList.value.length > 0 && (
                    <VanRow class="item">
                      <VanCol span={3}>
                        {/* 原来是 label，只借 `.info-box .item label` 的绿字 */}
                        <span class="text-[#07c160]">广告</span>
                      </VanCol>
                      <VanCol span={20}>
                        <VanSwipe
                          vertical
                          autoplay={5000}
                          showIndicators={false}
                          style={{ height: '19px' }}
                        >
                          {bannerList.value.map((b) => (
                            <VanSwipeItem key={b.id} style={{ height: '19px' }}>
                              <ExternalAnchor class="van-ellipsis block" url={b.url ?? ''}>
                                {b.title}
                              </ExternalAnchor>
                            </VanSwipeItem>
                          ))}
                        </VanSwipe>
                      </VanCol>
                    </VanRow>
                  )}

                  {bannerList.value.length > 0 && announcementList.value.length > 0 && (
                    <div class="border" />
                  )}

                  {announcementList.value.length > 0 && (
                    <VanRow class="item">
                      <VanCol span={3}>
                        <span class="text-[#07c160]">公告</span>
                      </VanCol>
                      <VanCol span={20}>
                        <VanSwipe
                          vertical
                          autoplay={5000}
                          showIndicators={false}
                          style={{ height: '19px' }}
                        >
                          {announcementList.value.map((a) => (
                            <VanSwipeItem key={a.id} style={{ height: '19px' }}>
                              <div class="van-ellipsis">{a.content}</div>
                            </VanSwipeItem>
                          ))}
                        </VanSwipe>
                      </VanCol>
                    </VanRow>
                  )}
                </div>
              )}
            </div>,
            bannerPeriod.value && (
              <RouterLink class="cansai btn-reset" to={awardRankingTo.value}>
                <div class="cansai-qu">
                  <div class="trophy-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="21"
                      height="20"
                      viewBox="0 0 21 20"
                      fill="none"
                    >
                      <g clip-path="url(#cansai-trophy-clip)">
                        <path
                          d="M16.0911 15.9565L20.0391 16.6542L15.6875 10.433L11.363 14.4938L15.229 20.0196L15.9266 16.0714L16.0911 15.9565Z"
                          fill="#D03B00"
                        />
                        <path
                          d="M4.81011 20.0196L8.67616 14.4938L4.35161 10.433L0 16.6542L3.94799 15.9565L4.11249 16.0714L4.81011 20.0196Z"
                          fill="#FF4B00"
                        />
                        <path
                          d="M16.8493 7.45595C17.4364 6.88064 17.5774 6.15259 17.3895 5.44809C17.2133 4.77876 16.7201 4.23876 15.9335 4.03909C16.1566 3.25249 15.9218 2.55961 15.4052 2.043C14.9237 1.56156 14.2192 1.32673 13.4442 1.54979C13.2446 0.763191 12.6928 0.242307 11.9883 0.0544095C11.319 -0.133335 10.5911 0.0310179 10.0274 0.60633C9.45206 0.0192456 8.73594 -0.121563 8.01952 0.0661818C7.35033 0.242307 6.81018 0.763191 6.61067 1.54979C5.82391 1.32673 5.13118 1.56156 4.61458 2.07817C4.12137 2.57138 3.90993 3.26411 4.12137 4.03909C3.33461 4.23876 2.84156 4.79053 2.65366 5.49503C2.47753 6.16437 2.63011 6.89226 3.20558 7.45595C2.61849 8.03126 2.47753 8.75931 2.66543 9.46381C2.84156 10.133 3.34639 10.6731 4.12137 10.8728C3.89831 11.6594 4.14491 12.3521 4.64974 12.8689C5.14296 13.3502 5.83569 13.585 6.61067 13.362C6.86889 14.36 7.70258 14.9001 8.59498 14.9001C9.11158 14.9001 9.6048 14.7122 10.0274 14.2779C10.6027 14.865 11.319 15.0058 12.0352 14.8179C12.7046 14.6418 13.2446 14.1487 13.4442 13.362C14.2192 13.585 14.9237 13.3502 15.4403 12.8336C15.9218 12.3405 16.1448 11.6478 15.9335 10.8728C16.7084 10.6731 17.2133 10.133 17.4012 9.41672C17.5774 8.74754 17.4246 8.01949 16.8493 7.45595Z"
                          fill="#FF9F00"
                        />
                        <path
                          d="M17.4012 9.41672C17.2133 10.133 16.7084 10.6731 15.9335 10.8728C16.1448 11.6478 15.9218 12.3405 15.4403 12.8336C14.9237 13.3502 14.2192 13.585 13.4442 13.362C13.2447 14.1487 12.7046 14.6418 12.0352 14.8179C11.319 15.0058 10.6027 14.865 10.0274 14.2779V0.60633C10.5911 0.0310179 11.319 -0.133335 11.9883 0.0544095C12.6928 0.242307 13.2446 0.763191 13.4442 1.54979C14.2192 1.32673 14.9237 1.56156 15.4052 2.043C15.9218 2.55961 16.1566 3.25234 15.9335 4.03909C16.7201 4.23876 17.2133 4.77876 17.3895 5.44809C17.5774 6.15259 17.4364 6.88064 16.8493 7.45595C17.4246 8.01949 17.5774 8.74754 17.4012 9.41672Z"
                          fill="#FF7816"
                        />
                        <path
                          d="M10.0274 2.17224C7.11549 2.17224 4.74361 4.54397 4.74361 7.456C4.74361 10.3679 7.11549 12.7398 10.0274 12.7398C12.9392 12.7398 15.3111 10.3679 15.3111 7.456C15.3111 4.54397 12.9394 2.17224 10.0274 2.17224Z"
                          fill="#FDBF00"
                        />
                        <path
                          d="M15.3111 7.456C15.3111 10.3679 12.9393 12.7398 10.0274 12.7398V2.17224C12.9394 2.17224 15.3111 4.54397 15.3111 7.456Z"
                          fill="#FF9F00"
                        />
                        <path
                          d="M13.5068 6.34068L11.0941 5.99011L10.0151 3.80383L8.93606 5.99011L6.52351 6.34068L8.26932 8.04246L7.85714 10.4454L10.0151 9.31096L12.1731 10.4454L11.7609 8.04246L13.5068 6.34068Z"
                          fill="#FF7816"
                        />
                        <path
                          d="M11.0941 5.99012L10.0274 3.82892V9.31739L12.1731 10.4454L11.761 8.04247L13.5068 6.34069L11.0941 5.99012Z"
                          fill="#FF4B00"
                        />
                      </g>
                      <defs>
                        <clipPath id="cansai-trophy-clip">
                          <rect width="20.0391" height="20" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </div>
                  <div class="name">
                    {bannerLotteryName.value}第<span>{bannerPeriod.value}</span>期参赛帖高手获奖榜单
                  </div>
                </div>
                <div class="more">
                  点击查看
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="8"
                    height="12"
                    viewBox="0 0 8 12"
                    fill="none"
                  >
                    <path
                      d="M1.29999 1L6.69999 6L1.29999 11"
                      stroke="white"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
              </RouterLink>
            )
          ]}

        {/* 三块 tab 一起贴顶吸附（offset-top 与 NavBar 高度对齐，对应 49208 同款）。
            用原生 CSS sticky 而不是 VanSticky —— Vant 的 unitToPx 不支持 calc()，
            而我们需要 top = NavBar 高度 + env(safe-area-inset-top)。 */}
        <div class="forum-sticky-bar">
          <div class="forum-lottery-bar">
            <div class="forum-lottery-tabs">
              {LOTTERY_FILTER_TABS.map((tab) => (
                <button
                  type="button"
                  key={tab.type}
                  class={['forum-lottery-tab btn-reset', store.lotteryFilter === tab.type ? 'active' : '']}
                  onClick={() => onLotteryFilterClick(tab.type)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div class="tjxzparts">
            {BOARDS.map((b) => (
              <button
                type="button"
                key={b.type}
                class={['allpart chosens btn-reset', store.board === b.type ? 'selected' : '']}
                onClick={() => onBoardChange(b.type)}
              >
                <span class="words">{b.name}</span>
              </button>
            ))}
          </div>

          <div class="tab-box">
            {/* 样式挂在 `.tab-box span` 上：span 保留，按钮/链接包在外面；「规则」是跳转 */}
            {SORT_TABS.map(({ type, name }) =>
              type === 4 ? (
                <RouterLink key={type} class="btn-reset flex" to="/forum/rules">
                  <span>{name}</span>
                </RouterLink>
              ) : (
                <button
                  type="button"
                  key={type}
                  class="btn-reset flex"
                  onClick={() => store.setSort(type)}
                >
                  <span class={{ act: store.sort === type }}>{name}</span>
                </button>
              ),
            )}
          </div>
        </div>

        <div class="forum-body-bd">
          <VanPullRefresh
            modelValue={refreshing.value}
            onUpdate:modelValue={(v: boolean) => (refreshing.value = v)}
            onRefresh={onRefresh}
          >
            <VanList
              loading={store.loading}
              finished={store.finished}
              finishedText="没有更多了"
              onLoad={onLoadMore}
            >
              <ul class="forum-list">
                {store.list.map((post) => (
                  <PostCard
                    key={post.postId}
                    post={post}
                    showContestBadge={store.board === 1}
                    concerned={followStore.isFollowing(post.userId)}
                    onGo={onGoDetail}
                    onToggle-concern={onToggleConcern}
                  />
                ))}
              </ul>
            </VanList>
          </VanPullRefresh>
        </div>
      </div>
    )
  },
})
